import { components } from "@/api-schema";
import { DeviceType } from "@/common/hooks/use-device";
import { CompanyLogo, CompanyLogoVariant } from "@/components/company-logo/company-logo";
import { automaticCellRenderer } from "@/components/data-table/_helpers/cell-renderer";
import { ImplementedAccessorColumnDef, ImplementedColumnDef } from "@/components/data-table/types";
import { EmptyValue } from "@/components/ui/empty-value";
import { renderCompanyType } from "@/modules/masterdata/company/_helpers/type-renderer";
import { getCompanySapIdentifier, getCompanyV1Identifier } from "@/modules/masterdata/company/company-identifiers";
import i18next from "i18next";
import { commonColumns } from "./_constants/common-columns";

export type CompanyTableType = components["schemas"]["Company"];

type CompanyName = CompanyTableType["name"];
type CompanyType = CompanyTableType["type"];
type CompanyGroup = CompanyTableType["companyGroup"];
type CompanyIdentifierValue = string | undefined;
type CompanyLegalText = string | null | undefined;

export const companyTableDefaultColumns = {
  columnMobileSummary: {
    id: "mobileSummary",
    exportPaths: [],
    size: 320,
    visibility: [DeviceType.Mobile],
    enableSorting: false,
    header: () => i18next.t("common:Company"),
    cell: ({ row }) => {
      const company = row.original;
      const companyType = company.type ? renderCompanyType(company.type) : undefined;
      const companyGroup = company.companyGroup?.name;
      const sapPrefix = getCompanySapIdentifier(company);
      const v1Id = getCompanyV1Identifier(company);

      return (
        <div className="flex w-full flex-col gap-2">
          <div className="flex items-center gap-2">
            <CompanyLogo company={company} variant={CompanyLogoVariant.COMPACT} className="shrink-0" />
            <div className="min-w-0 font-medium wrap-break-word">{automaticCellRenderer(company.name)}</div>
          </div>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <div>
              <dt className="text-muted-foreground">{i18next.t("masterdata:Type")}</dt>
              <dd>{automaticCellRenderer(companyType)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{i18next.t("masterdata:CompanyGroup")}</dt>
              <dd>{companyGroup ? automaticCellRenderer(companyGroup) : <EmptyValue />}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{i18next.t("masterdata:CompanySapPrefix")}</dt>
              <dd>{sapPrefix ? automaticCellRenderer(sapPrefix) : <EmptyValue />}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{i18next.t("masterdata:CompanyVatId")}</dt>
              <dd>{automaticCellRenderer(company.vatId)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{i18next.t("masterdata:V1ID")}</dt>
              <dd>{v1Id ? automaticCellRenderer(v1Id) : <EmptyValue />}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{i18next.t("common:TechnicalId")}</dt>
              <dd>{automaticCellRenderer(company.technicalId)}</dd>
            </div>
          </dl>
        </div>
      );
    },
  } as ImplementedColumnDef<CompanyTableType, unknown>,

  columnName: {
    id: "name",
    size: 260,
    enableSorting: true,
    align: "left",
    header: () => i18next.t("common:Company"),
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: CompanyTableType) => row.name,
    cell: (cell) => {
      const company = cell.row.original;
      return (
        <div className="flex items-center gap-3">
          <CompanyLogo company={company} className="shrink-0" />
          <div className="min-w-0 font-medium wrap-break-word">{automaticCellRenderer(company.name)}</div>
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<CompanyTableType, CompanyName>,

  columnV1Id: {
    id: "v1Id",
    exportPaths: ["companyIdentifiers[type=V1].identifier"],
    size: 130,
    enableSorting: false,
    align: "left",
    header: () => i18next.t("masterdata:V1ID"),
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: CompanyTableType) => getCompanyV1Identifier(row),
    cell: (cell) => {
      const value = cell.getValue();
      return value ? automaticCellRenderer(value) : <EmptyValue />;
    },
  } as ImplementedAccessorColumnDef<CompanyTableType, CompanyIdentifierValue>,

  columnCompanyGroup: {
    id: "companyGroup",
    exportPaths: ["companyGroup.name"],
    size: 150,
    enableSorting: true,
    align: "left",
    header: () => i18next.t("masterdata:CompanyGroup"),
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: CompanyTableType) => row.companyGroup,
    cell: (cell) => {
      const companyGroup = cell.getValue() as CompanyGroup | null | undefined;
      return companyGroup?.name ?? <EmptyValue />;
    },
  } as ImplementedAccessorColumnDef<CompanyTableType, CompanyGroup | null | undefined>,

  columnTechnicalId: {
    ...commonColumns.columnTechnicalId<CompanyTableType>(),
    align: "left",
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
  } as ImplementedAccessorColumnDef<CompanyTableType, string | undefined>,

  columnType: {
    id: "type",
    size: 140,
    enableSorting: true,
    align: "left",
    header: () => i18next.t("masterdata:Type"),
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: CompanyTableType) => row.type,
    cell: (cell) => {
      const value = cell.getValue() as CompanyType | null | undefined;
      return automaticCellRenderer(value ? renderCompanyType(value) : undefined);
    },
  } as ImplementedAccessorColumnDef<CompanyTableType, CompanyType | null | undefined>,

  columnSapPrefix: {
    id: "sapPrefix",
    exportPaths: ["companyIdentifiers[type=SAP].identifier"],
    size: 140,
    enableSorting: false,
    align: "left",
    header: () => i18next.t("masterdata:CompanySapPrefix"),
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: CompanyTableType) => getCompanySapIdentifier(row),
    cell: (cell) => {
      const value = cell.getValue();
      return value ? automaticCellRenderer(value) : <EmptyValue />;
    },
  } as ImplementedAccessorColumnDef<CompanyTableType, CompanyIdentifierValue>,

  columnCommercialRegisterCourt: {
    id: "commercialRegisterCourt",
    size: 220,
    enableSorting: true,
    header: () => i18next.t("masterdata:CompanyCommercialRegisterCourt"),
    visibility: [DeviceType.Widescreen],
    accessorFn: (row: CompanyTableType) => row.commercialRegisterCourt,
    cell: (cell) => automaticCellRenderer(cell.getValue() as CompanyLegalText),
  } as ImplementedAccessorColumnDef<CompanyTableType, CompanyLegalText>,

  columnCommercialRegisterNumber: {
    id: "commercialRegisterNumber",
    size: 160,
    enableSorting: true,
    header: () => i18next.t("masterdata:CompanyCommercialRegisterNumber"),
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: CompanyTableType) => row.commercialRegisterNumber,
    cell: (cell) => automaticCellRenderer(cell.getValue() as CompanyLegalText),
  } as ImplementedAccessorColumnDef<CompanyTableType, CompanyLegalText>,

  columnVatId: {
    id: "vatId",
    size: 140,
    enableSorting: true,
    align: "left",
    header: () => i18next.t("masterdata:CompanyVatId"),
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row: CompanyTableType) => row.vatId,
    cell: (cell) => automaticCellRenderer(cell.getValue() as CompanyLegalText),
  } as ImplementedAccessorColumnDef<CompanyTableType, CompanyLegalText>,

  columnUuid: commonColumns.columnUuid<CompanyTableType>(),
  columnDeletedAt: {
    ...commonColumns.columnDeletedAt<CompanyTableType>(),
    align: "left",
  } as ImplementedAccessorColumnDef<CompanyTableType, string | undefined>,
  columnCreatedAt: commonColumns.columnCreatedAt<CompanyTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<CompanyTableType>(),
};
